import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FirebaseTSStorage } from 'firebasets/firebasetsStorage/firebaseTSStorage';
import { FirebaseTSFirestore } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';

@Component({
  selector: 'app-create-story',
  templateUrl: './create-story.component.html',
  styleUrls: ['./create-story.component.css']
})
export class CreateStoryComponent {
  selectedImage: string | ArrayBuffer | null = null;
  selectedImageFile: File | null = null;
  selectedVideo: string | ArrayBuffer | null = null;
  selectedVideoFile: File | null = null;
  storyText: string = '';
  textColor: string = '#ffffff';
  textPosition: { x: number, y: number } = { x: 50, y: 50 };
  isDragging: boolean = false;
  isLoading: boolean = false;

  storage = new FirebaseTSStorage();
  firestore = new FirebaseTSFirestore();
  auth = new FirebaseTSAuth();

  constructor(
    public dialogRef: MatDialogRef<CreateStoryComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type.startsWith('image/')) {
      this.selectedImageFile = file;
      this.selectedVideo = null;
      this.selectedVideoFile = null;
      this.generatePreview(file, 'image');
    } else if (file.type.startsWith('video/')) {
      this.selectedVideoFile = file;
      this.selectedImage = null;
      this.selectedImageFile = null;
      this.generatePreview(file, 'video');
    }
  }

  generatePreview(file: File, type: 'image' | 'video'): void {
    const reader = new FileReader();
    reader.onload = () => {
      if (type === 'image') {
        this.selectedImage = reader.result;
      } else {
        this.selectedVideo = reader.result;
      }
    };
    reader.readAsDataURL(file);
  }

  onDragStart(event: MouseEvent): void {
    this.isDragging = true;
  }

  onDrag(event: MouseEvent): void {
    if (!this.isDragging) return;

    const container = event.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();
    
    this.textPosition = {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100
    };
  }

  onDragEnd(): void {
    this.isDragging = false;
  }

  adjustTextareaHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  clearMedia() {
    this.selectedImage = null;
    this.selectedImageFile = null;
    this.selectedVideo = null;
    this.selectedVideoFile = null;
  }

  async publishStory(): Promise<void> {
    if (!this.selectedImageFile && !this.selectedVideoFile) return;

    const file = this.selectedImageFile || this.selectedVideoFile;
    const type = this.selectedImageFile ? 'image' : 'video';
    const userId = this.auth.getAuth().currentUser?.uid;

    if (!userId || !file) return;

    this.isLoading = true;

    try {
      // Upload du média
      const uploadPromise = new Promise((resolve, reject) => {
        this.storage.upload({
          uploadName: `stories/${userId}_${Date.now()}`,
          path: ["Stories", userId, file.name],
          data: {
            data: file
          },
          onComplete: (downloadUrl) => resolve(downloadUrl),
          onFail: (error) => reject(error)
        });
      });

      const downloadUrl = await uploadPromise;

      // Création de la story dans Firestore
      await new Promise((resolve, reject) => {
        this.firestore.create({
          path: ["Users", userId, "stories"],
          data: {
            type: type,
            url: downloadUrl,
            timestamp: Date.now(),
            text: this.storyText || null,
            textColor: this.textColor || '#ffffff',
            textPosition: this.textPosition || { x: 50, y: 50 },
            userId: userId
          },
          onComplete: () => {
            this.dialogRef.close();
            resolve(null);
          },
          onFail: (error) => reject(error)
        });
      });
    } catch (error) {
      console.error("Erreur lors de la création de la story:", error);
    } finally {
      this.isLoading = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
